import React, { useState, useCallback, useEffect } from 'react';
import { Form, Input, Select, Row, Col, Button, Switch, message, Divider, Skeleton } from "antd";
import { PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { getCredentials, getCredentialResources } from '../../Credentials/CredentialsServices';
import { getAgents } from '../../Agents/AgentServices';
import { useErrorHandler } from '../../../Hooks/useErrorHandler';

const ChannelForm = ({ form, initialValues, isEdit = false, onCreateCredentialRequest }) => {
    const { t } = useTranslation();
    const { handleError } = useErrorHandler();
    const [credentials, setCredentials] = useState([]);
    const [agents, setAgents] = useState([]);
    const [resources, setResources] = useState([]);
    const [credentialsLoading, setCredentialsLoading] = useState(false);
    const [agentsLoading, setAgentsLoading] = useState(false);
    const [resourcesLoading, setResourcesLoading] = useState(false);
    const [resourcesFetched, setResourcesFetched] = useState(false);

    // Watch for credential changes
    const selectedCredentialId = Form.useWatch('credential_id', form);

    // Channel types - matching backend allowed values
    const channelTypes = [
        { value: 'facebook_page', label: t('facebook_page') },
        { value: 'instagram', label: `${t('instagram')} (${t('coming_soon')})`, disabled: true },
        { value: 'whatsapp', label: `${t('whatsapp')} (${t('coming_soon')})`, disabled: true },
    ];

    // Fetch credentials
    const fetchCredentials = useCallback(async () => {
        setCredentialsLoading(true);
        try {
            const response = await getCredentials({ page: 1, size: 100 });
            setCredentials(response.data || []);
        } catch (error) {
            handleError(error);
        } finally {
            setCredentialsLoading(false);
        }
    }, [handleError]);

    // Fetch agents
    const fetchAgents = useCallback(async () => {
        setAgentsLoading(true);
        try {
            const response = await getAgents({ page: 1, size: 100 });
            setAgents(response.data || []);
        } catch (error) {
            handleError(error);
        } finally {
            setAgentsLoading(false);
        }
    }, [handleError]);

    // Fetch resources when credential changes
    useEffect(() => {
        if (!selectedCredentialId || isEdit) {
            setResources([]);
            setResourcesFetched(true);
            return;
        }

        const fetchResources = async () => {
            setResourcesFetched(false);
            setResourcesLoading(true);
            try {
                // If we are editing, we might want to preserve the current value even if not in list (though it should be)
                // For now just fetching everything
                const response = await getCredentialResources({
                    credential_id: selectedCredentialId,
                    page: 1,
                    size: 100
                });
                setResources(response.data || []);
            } catch (error) {
                // Squelch 404s if no resources found, or handle error
                // handleError(error); 
                console.error("Failed to fetch resources", error);
                setResources([]);
            } finally {
                setResourcesLoading(false);
                setResourcesFetched(true);
            }
        };

        fetchResources();
    }, [selectedCredentialId]);

    useEffect(() => {
        fetchCredentials();
        fetchAgents();
    }, [fetchCredentials, fetchAgents]);

    const credentialOptions = credentials.map(cred => ({
        value: cred.id,
        label: `${cred.name || cred.email || cred.credential_user_id || 'Unnamed'} (${cred.channel_type})`,
    }));

    const agentOptions = agents.map(agent => ({
        value: agent.id,
        label: agent.name,
    }));

    // Resource options (Pages/Accounts)
    const resourceOptions = resources.map(res => ({
        value: res.id,
        label: res.name,
    }));

    return (
        <Form form={form} layout="vertical" initialValues={initialValues}>
            <Row gutter={16}>
                {/* Credential & Channel Type Row */}
                <Col xs={24} md={12}>
                    <Form.Item
                        label={t('credential')}
                        name="credential_id"
                        rules={[{ required: true, message: t('please_select_credential') }]}
                    >
                        <Select
                            placeholder={t('select_credential')}
                            loading={credentialsLoading}
                            options={credentialOptions}
                            dropdownRender={(menu) => (
                                <>
                                    {menu}
                                    <Divider style={{ margin: '8px 0' }} />
                                    <Button
                                        type="text"
                                        icon={<PlusOutlined />}
                                        onClick={onCreateCredentialRequest}
                                        block
                                    >
                                        {t('create_new_credential')}
                                    </Button>
                                </>
                            )}
                            onChange={() => {
                                // Clear external ID when credential changes since resources will change
                                form.setFieldsValue({ external_channel_id: null });
                            }}
                        />
                    </Form.Item>
                </Col>

                {!isEdit && (
                    <Col xs={24} md={12}>
                        <Form.Item
                            label={t('channel_type')}
                            name="channel_type"
                            rules={[{ required: true, message: t('please_select_channel_type') }]}
                        >
                            <Select
                                placeholder={t('select_channel_type')}
                                options={channelTypes}
                            />
                        </Form.Item>
                    </Col>
                )}

                {/* External ID (Create) / Name (Edit) Row */}
                <Col xs={24} md={12}>
                    {isEdit ? (
                        <Form.Item
                            label={t('channel_name')}
                            name="name"
                            rules={[
                                { required: true, message: t('please_enter_channel_name') },
                                { max: 255, message: t('max_255_characters') }
                            ]}
                        >
                            <Input placeholder={t('enter_channel_name')} disabled={isEdit} />
                        </Form.Item>
                    ) : (
                        <Form.Item
                            label={t('resource_name')}
                            name="external_channel_id"
                            rules={[
                                { required: true, message: t('please_select_resource') },
                            ]}
                            tooltip={t('external_channel_id_tooltip', { defaultValue: 'Select the Page, Account, or Number you want to connect' })}
                        >
                            {selectedCredentialId && !resourcesFetched ? (
                                <Skeleton.Input active style={{ width: '100%', height: 32 }} size="default" />
                            ) : (
                                <Select
                                    placeholder={selectedCredentialId ? t('select_page_or_account') : t('select_credential_first')}
                                    options={resourceOptions}
                                    loading={resourcesLoading}
                                    disabled={!selectedCredentialId}
                                    showSearch
                                    optionFilterProp="children"
                                />
                            )}
                        </Form.Item>
                    )}
                </Col>



                {/* Agent & Sync Row */}
                <Col xs={24} md={12}>
                    <Form.Item
                        label={t('agent')}
                        name="agent_id"
                        tooltip={t('agent_assignment_tooltip', { defaultValue: 'Optionally assign an agent to handle messages from this channel' })}
                    >
                        <Select
                            placeholder={t('select_agent')}
                            loading={agentsLoading}
                            options={agentOptions}
                            allowClear
                        />
                    </Form.Item>
                </Col>

                <Col xs={24} md={12}>
                    <Form.Item
                        label={t('synchronization')}
                        name="is_synchronization_enabled"
                        valuePropName="checked"
                        tooltip={t('synchronization_tooltip', { defaultValue: 'Enable automatic synchronization of messages from this channel' })}
                    >
                        <Switch />
                    </Form.Item>
                </Col>

                {/* Description Field - Available in both create and edit modes */}
                <Col xs={24}>
                    <Form.Item
                        label={t('channel_description')}
                        name="description"
                        rules={[
                            { max: 4096, message: t('max_4096_characters') }
                        ]}
                    >
                        <Input.TextArea
                            placeholder={t('enter_channel_description')}
                            rows={4}
                            showCount
                            maxLength={4096}
                            disabled={isEdit}
                        />
                    </Form.Item>
                </Col>

            </Row>
        </Form>
    );
};

export default ChannelForm;
